import React, { lazy, useMemo } from 'react';
import { deleteIdea, disqualify, IdeaOptions, IdeaPayload, markQualified, unarchiveIdea, updateIdeas } from '../../services/idea.service';
import dayjs from 'dayjs';
import { Markdown } from '../Markdown/Markdown';
import { Link } from 'react-router-dom';
import { useDesktopMode } from '../../services/hooks/useDesktopMode';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import Alert from '@mui/material/Alert/Alert';
import Stack from '@mui/material/Stack/Stack';
import Tooltip from '@mui/material/Tooltip/Tooltip';
import Button from '@mui/material/Button/Button';
import IconButton from '@mui/material/IconButton/IconButton';
import TextField from '@mui/material/TextField/TextField';
import Dialog from '@mui/material/Dialog/Dialog';
import DialogTitle from '@mui/material/DialogTitle/DialogTitle';
import DialogContent from '@mui/material/DialogContent/DialogContent';
import DialogContentText from '@mui/material/DialogContentText/DialogContentText';
import DialogActions from '@mui/material/DialogActions/DialogActions';
import Typography from '@mui/material/Typography/Typography';

const Editor = lazy(() => import('../Editor/Editor'));
export const IdeaSummary = ({
    hackathonId,
    idea,
    isInTeam,
    isOrganizer,
    canEdit,
    canDelete,
    onEdit,
    onDelete,
    onUnarchive
}: {
    hackathonId: string
    idea: IdeaPayload
    isInTeam: boolean
    isOrganizer: boolean
    canDelete?: boolean
    canEdit?: boolean
    onDelete?: () => void
    onEdit?: () => void
    onUnarchive?: () => void
}) => {

    const desktopMode = useDesktopMode();

    // Markdown sanitizes the rendered HTML; sanitizing the markdown source here ate text like "<owner>"
    const description = idea.description;
    const submittedTime = useMemo(() => dayjs(idea.submittedAt).fromNow(), [idea.submittedAt]);
    const updatedTime = useMemo(() => dayjs(idea.lastUpdatedAt).fromNow(), [idea.lastUpdatedAt]);

    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<{ message: string } | null>(null);
    const [conflict, setConflict] = React.useState(false);

    const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);
    const [showEditForm, setShowEditForm] = React.useState(false);

    const [editIdea, setEditIdea] = React.useState<IdeaOptions>({
        title: idea.title,
        description: idea.description
    });

    return (
        <>

            {
                error && <Alert severity='error'>{error.message}</Alert>
            }
            {
                !showEditForm && <Stack spacing={2} direction="column">

                    {
                        idea.disqualified && <Alert severity="error">Disqualified</Alert>
                    }
                    {
                        idea.archived && <Alert severity="warning">Archived</Alert>
                    }
                    <Stack spacing={2} direction="row" alignItems="center" justifyContent={'space-between'}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                            {
                                isInTeam && <Tooltip title={'My team'}>
                                    <StarIcon color='primary' />
                                </Tooltip>
                            }

                            <Typography variant="h4" component="h3">
                                <Link to={`/h/${hackathonId}/idea/${idea._id}`} style={{ color: 'inherit', textDecoration: 'none', overflowWrap: 'anywhere' }}>{idea.title}</Link>
                            </Typography>
                        </Stack>
                        <Stack spacing={1} direction="row">
                            {
                                desktopMode && isOrganizer && idea.disqualified && <Button onClick={() => {
                                    setLoading(true);
                                    markQualified(hackathonId, idea._id).then(() => {
                                        onEdit && onEdit();
                                    }, (err) => {
                                        setError(err);
                                    }).finally(() => {
                                        setLoading(false);
                                    })
                                }}>Requalify</Button>
                            }
                            {
                                desktopMode && isOrganizer && !idea.disqualified && <Button color="error" onClick={() => {
                                    setLoading(true);
                                    disqualify(hackathonId, idea._id).then(() => {
                                        onEdit && onEdit();
                                    }, (err) => {
                                        setError(err);
                                    }).finally(() => {
                                        setLoading(false);
                                    })
                                }}>Disqualify</Button>
                            }
                            {
                                desktopMode && canDelete && !idea.archived && <Button disabled={loading} color={'error'} onClick={() => {
                                    // show a confirmation dialog
                                    setShowDeleteConfirmation(true);
                                }}>Delete</Button>
                            }
                            {
                                desktopMode && idea.archived && isOrganizer && <Button onClick={() => {
                                    // unarchive the idea
                                    unarchiveIdea(hackathonId, idea._id).then(() => {
                                        onUnarchive && onUnarchive();
                                    });
                                }}>Unarchive</Button>
                            }
                            {
                                desktopMode && canEdit && <Button onClick={() => {
                                    // edit the idea
                                    setEditIdea({
                                        title: idea.title,
                                        description: idea.description,
                                        lastUpdatedAt: idea.lastUpdatedAt
                                    })
                                    setConflict(false);
                                    setShowEditForm(true);
                                }}>Edit</Button>
                            }
                            {
                                // phones: Edit says what it does and comes first; the trash icon sits apart so a tap meant for Edit can't land on it
                                !desktopMode && canEdit && <Button size="small" startIcon={<EditIcon />} onClick={() => {
                                    setEditIdea({
                                        title: idea.title,
                                        description: idea.description,
                                        lastUpdatedAt: idea.lastUpdatedAt
                                    })
                                    setConflict(false);
                                    setShowEditForm(true);
                                }}>Edit</Button>
                            }
                            {
                                !desktopMode && canDelete && !idea.archived && <IconButton color="error" aria-label="Delete idea" sx={{ ml: 2 }} onClick={() => {
                                    // show a confirmation dialog
                                    setShowDeleteConfirmation(true);
                                }}>
                                    <DeleteIcon />
                                </IconButton>
                            }

                        </Stack>

                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: '4px !important' }}>
                        Submitted {submittedTime} by <b>{idea.createdBy.username}</b>{updatedTime !== submittedTime && ` · edited ${updatedTime}`}
                    </Typography>
                    {
                        description?.trim()
                            ? <div><Markdown markdown={description} /></div>
                            : <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>No pitch yet.</Typography>
                    }

                </Stack>
            }
            {
                showEditForm && <Stack spacing={2} direction="column">
                    <TextField label="Title" value={editIdea.title} inputProps={{ maxLength: 120 }}
                        error={!editIdea.title.trim()} helperText={!editIdea.title.trim() ? 'Your idea needs a title' : `${editIdea.title.length}/120`}
                        onChange={(e) => setEditIdea(current => ({ ...current, title: e.target.value }))} />
                    <React.Suspense fallback={<div>Loading...</div>}>
                        <Editor toolbar={true} value={editIdea.description} onChange={(value) => setEditIdea(current => ({ ...current, description: value }))} />
                    </React.Suspense>
                    {
                        // right above Save, which it disables: a long pitch would push a message at the top out of view
                        conflict && <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => {
                            // start again from the latest saved version; the user's text is still on screen until they click
                            onEdit && onEdit();
                            setShowEditForm(false);
                            setConflict(false);
                            setError(null);
                        }}>Discard mine</Button>}>
                            A teammate saved this idea after you started editing, so <b>Save</b> is off: saving now would overwrite their changes.
                            Copy anything you want to keep, click <b>Discard mine</b> to load their version, then edit again.
                        </Alert>
                    }
                    <Stack direction={'row'} spacing={2}>
                        <Button variant="contained" disabled={loading || conflict || !editIdea.title.trim()} onClick={() => {
                            setError(null);
                            // update the idea
                            setLoading(true);
                            updateIdeas(hackathonId, idea._id, editIdea).then(() => {
                                setShowEditForm(false);
                                onEdit && onEdit();
                            }, (err) => {
                                if (err?.response?.status === 409) {
                                    setConflict(true);
                                } else {
                                    setError({ message: err?.response?.data?.message || 'Could not save the idea' });
                                }
                            }).finally(() => {
                                setLoading(false);
                            })
                        }}>Save</Button>
                        <Button disabled={loading} onClick={() => {
                            setShowEditForm(false);
                            setError(null);
                        }}>Cancel</Button>
                    </Stack>
                </Stack>
            }
            <DeleteConfirmation
                open={showDeleteConfirmation}
                onConfirm={() => {
                    // delete the idea
                    deleteIdea(hackathonId, idea._id).then(() => {
                        onDelete && onDelete();
                    }, (err) => {
                        setError(err);
                    }).finally(() => {
                        setShowDeleteConfirmation(false);
                        setLoading(false);
                    })
                }}
                onCancel={() => setShowDeleteConfirmation(false)} />
        </>

    )
}

const DeleteConfirmation = ({
    open,
    onConfirm,
    onCancel
}: {
    open: boolean,
    onConfirm: () => void,
    onCancel: () => void
}) => {
    return (
        <Dialog open={open}>
            <DialogTitle>Delete Idea</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Are you sure you want to delete this idea?
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                <Button color="error" variant="contained" onClick={onConfirm}>Delete</Button>
            </DialogActions>
        </Dialog>
    )
}